/**
 * Push Notification Utility for Tend
 * Handles service worker registration, subscription, and permission management
 */

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Convert VAPID public key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Get current notification permission status
export function getNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission; // 'granted', 'denied', or 'default'
}

// Register service worker
export async function registerServiceWorker() {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });
    console.log('Service Worker registered:', registration.scope);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

// Request notification permission
export async function requestNotificationPermission() {
  if (!isPushSupported()) {
    return { success: false, permission: 'unsupported' };
  }
  
  try {
    const permission = await Notification.requestPermission();
    return { success: permission === 'granted', permission };
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return { success: false, permission: 'error', error: error.message };
  }
}

// Subscribe to push notifications
export async function subscribeToPush(token) {
  console.log('[Push] Starting subscription process...');
  
  if (!isPushSupported()) {
    console.error('[Push] Not supported');
    throw new Error('Push notifications not supported');
  }
  
  // Check permission
  console.log('[Push] Current permission:', Notification.permission);
  if (Notification.permission !== 'granted') {
    console.log('[Push] Requesting permission...');
    const result = await requestNotificationPermission();
    console.log('[Push] Permission result:', result);
    if (!result.success) {
      throw new Error('Notification permission denied');
    }
  }
  
  // Get service worker registration
  console.log('[Push] Getting service worker...');
  let registration = await navigator.serviceWorker.ready;
  if (!registration) {
    console.log('[Push] No registration, registering...');
    registration = await registerServiceWorker();
    if (!registration) {
      throw new Error('Service worker registration failed');
    }
    registration = await navigator.serviceWorker.ready;
  }
  console.log('[Push] Service worker ready:', registration.scope);
  
  // Get VAPID public key from backend
  console.log('[Push] Fetching VAPID key...');
  const keyResponse = await fetch(`${API}/api/push/vapid-public-key`);
  if (!keyResponse.ok) {
    console.error('[Push] Failed to get VAPID key:', keyResponse.status);
    throw new Error('Failed to get VAPID key');
  }
  const { publicKey } = await keyResponse.json();
  console.log('[Push] Got VAPID key:', publicKey.substring(0, 20) + '...');
  
  // Subscribe to push
  console.log('[Push] Subscribing to push manager...');
  
  const existingSub = await registration.pushManager.getSubscription();
  if (existingSub) {
      await existingSub.unsubscribe().catch(()=>console.log('Error unsubscribing old token'));
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey)
  });
  console.log('[Push] Subscription created:', subscription.endpoint.substring(0, 50) + '...');
  
  // Get device name
  const deviceName = getDeviceName();
  console.log('[Push] Device name:', deviceName);
  
  // Send subscription to backend
  console.log('[Push] Sending to backend...');
  const response = await fetch(`${API}/api/push/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      subscription: subscription.toJSON(),
      device_name: deviceName
    })
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error('[Push] Backend error:', response.status, errorText);
    throw new Error('Failed to save subscription');
  }
  
  const result = await response.json();
  console.log('[Push] Backend response:', result);
  
  return { success: true, subscription };
}

// Check and refresh expired subscriptions silently
export async function checkAndRefreshSubscription(token) {
  if (!isPushSupported() || Notification.permission !== 'granted') return;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      const deviceName = getDeviceName();
      await fetch(`${API}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ subscription: subscription.toJSON(), device_name: deviceName })
      }).catch(() => {});
    }
  } catch (e) {
    console.error('Silent sub refresh failed', e);
  }
}


// Unsubscribe from push notifications
export async function unsubscribeFromPush(token) {
  if (!isPushSupported()) return { success: true };
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      // Unsubscribe from browser
      await subscription.unsubscribe();
      
      // Remove from backend
      await fetch(`${API}/api/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint
        })
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error unsubscribing:', error);
    return { success: false, error: error.message };
  }
}

// Check if user is currently subscribed
export async function isSubscribed() {
  if (!isPushSupported()) return false;
  
  try {
    // First check if service worker is registered
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return false;
    
    // Then check for active subscription
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    console.error('Error checking subscription:', error);
    return false;
  }
}

// Send test notification
export async function sendTestNotification(token) {
  const response = await fetch(`${API}/api/push/test`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to send test notification');
  }
  
  return response.json();
}

// Get device name for identification
function getDeviceName() {
  const ua = navigator.userAgent;
  
  // Check for mobile devices
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) {
    if (/Mobile/.test(ua)) return 'Android Phone';
    return 'Android Tablet';
  }
  
  // Check for desktop browsers
  if (/Chrome/.test(ua)) return 'Chrome Browser';
  if (/Firefox/.test(ua)) return 'Firefox Browser';
  if (/Safari/.test(ua)) return 'Safari Browser';
  if (/Edge/.test(ua)) return 'Edge Browser';
  
  return 'Web Browser';
}

// Initialize push notifications on app load
export async function initializePush() {
  if (!isPushSupported()) {
    console.log('Push notifications not supported on this browser');
    return false;
  }
  
  try {
    await registerServiceWorker();
    return true;
  } catch (error) {
    console.error('Failed to initialize push:', error);
    return false;
  }
}
